import axios from 'axios'
import {
  ListingsResponse,
  SingleListingResponse,
  StatsResponse,
  CategoriesResponse,
  ProfilesResponse,
  UploadResponse,
  CreateListingPayload,
  MarketplaceListing,
  ListingStatus,
  ListingType
} from './_models'

const API_URL = process.env.REACT_APP_API_URL || ''
const MARKETPLACE_URL = `${API_URL}/api/marketplace`

// Get auth token from localStorage
const getAuthToken = () => {
  const auth = localStorage.getItem('kt-auth-react-v')
  if (auth) {
    const parsed = JSON.parse(auth)
    return parsed.api_token
  }
  return null
}

// Create axios instance with auth header
const getAxios = () => {
  const token = getAuthToken()
  return axios.create({
    headers: {
      Authorization: token ? `Bearer ${token}` : ''
    }
  })
}

// Get all listings with optional filters
export const getListings = async (params?: {
  status?: ListingStatus | 'all'
  listing_type?: ListingType | 'all'
  profile_id?: string
  page?: number
  limit?: number
}): Promise<ListingsResponse> => {
  const api = getAxios()
  const response = await api.get<ListingsResponse>(`${MARKETPLACE_URL}/listings`, { params })
  return response.data
}

// Get single listing by ID
export const getListing = async (id: string): Promise<SingleListingResponse> => {
  const api = getAxios()
  const response = await api.get<SingleListingResponse>(`${MARKETPLACE_URL}/listings/${id}`)
  return response.data
}

// Create new listing
export const createListing = async (data: CreateListingPayload): Promise<SingleListingResponse> => {
  const api = getAxios()
  const response = await api.post<SingleListingResponse>(`${MARKETPLACE_URL}/listings`, data)
  return response.data
}

// Update listing
export const updateListing = async (id: string, data: Partial<CreateListingPayload>): Promise<SingleListingResponse> => {
  const api = getAxios()
  const response = await api.put<SingleListingResponse>(`${MARKETPLACE_URL}/listings/${id}`, data)
  return response.data
}

// Delete listing
export const deleteListing = async (id: string): Promise<{success: boolean; message: string}> => {
  const api = getAxios()
  const response = await api.delete<{success: boolean; message: string}>(`${MARKETPLACE_URL}/listings/${id}`)
  return response.data
}

// Get marketplace stats
export const getStats = async (): Promise<StatsResponse> => {
  const api = getAxios()
  const response = await api.get<StatsResponse>(`${MARKETPLACE_URL}/stats`)
  return response.data
}

// Get categories
export const getCategories = async (): Promise<CategoriesResponse> => {
  const api = getAxios()
  const response = await api.get<CategoriesResponse>(`${MARKETPLACE_URL}/categories`)
  return response.data
}

// Get available profiles
export const getProfiles = async (): Promise<ProfilesResponse> => {
  const api = getAxios()
  const response = await api.get<ProfilesResponse>(`${MARKETPLACE_URL}/profiles`)
  return response.data
}

// Get Israeli cities for location selection
export interface IsraeliCity {
  name: string
  lat: number
  lng: number
}

export const getCities = async (): Promise<{ success: boolean; cities: IsraeliCity[] }> => {
  const api = getAxios()
  const response = await api.get<{ success: boolean; cities: IsraeliCity[] }>(`${MARKETPLACE_URL}/cities`)
  return response.data
}

// Get category attributes (dynamic fields)
export const getCategoryAttributes = async (categoryId: string): Promise<{success: boolean; attributes: any[]}> => {
  const api = getAxios()
  const response = await api.get<{success: boolean; attributes: any[]}>(`${MARKETPLACE_URL}/category-attributes/${categoryId}`)
  return response.data
}

// Upload photos
export const uploadPhotos = async (files: File[]): Promise<UploadResponse> => {
  const api = getAxios()
  const formData = new FormData()
  files.forEach(file => {
    formData.append('photos', file)
  })
  const response = await api.post<UploadResponse>(`${MARKETPLACE_URL}/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${getAuthToken()}`
    }
  })
  return response.data
}

// Queue listing for posting (change status to pending)
export const queueForPosting = async (id: string): Promise<SingleListingResponse> => {
  return updateListing(id, { status: 'pending' })
}

// Save as draft
export const saveAsDraft = async (id: string): Promise<SingleListingResponse> => {
  return updateListing(id, { status: 'draft' })
}

// ===== Facebook Synced Listings =====

export interface FacebookProfile {
  id: string
  facebook_id: string
  name: string
  is_logged_in: boolean
  last_health_check: string | null
  synced_listings_count: number
  last_synced_at: string | null
  profile_picture?: string
}

export interface FacebookProfilesResponse {
  success: boolean
  profiles: FacebookProfile[]
}

export interface SyncResponse {
  success: boolean
  message?: string
  listings_count?: number
  timestamp?: string
  error?: string
}

export interface FacebookSyncedListing {
  _id: string
  account_id: string
  profile_id: string
  facebook_profile_id: string
  facebook_listing_id: string
  facebook_listing_set_id?: string
  title: string
  description?: string
  price: number
  currency?: string
  formatted_price: string
  category_id?: string
  category_name?: string
  primary_photo_url: string
  photo_urls?: string[]
  is_live: boolean
  is_sold: boolean
  is_pending: boolean
  is_draft?: boolean
  is_hidden?: boolean
  listing_is_rejected?: boolean
  listing_type?: string
  // Insights
  views_count: number
  messages_count?: number
  saves_count?: number
  shares_count?: number
  // Item attributes
  condition?: string
  brand?: string
  color?: string
  material?: string
  size?: string
  model?: string
  // Price extras
  comparable_price?: number
  is_negotiable?: boolean
  // Hashtags
  hashtags?: string[]
  // Location
  location?: string
  latitude?: number
  longitude?: number
  // Comments
  comments?: {
    comment_id: string
    author_name: string
    author_id: string
    text: string
    created_time: string
  }[]
  comments_count?: number
  // Conversations/Messages
  conversations?: {
    thread_id: string
    thread_fbid?: string  // The ID used in /messages/t/{thread_fbid} URL
    participant_name: string
    participant_id: string
    last_message: string
    last_message_time: string
    unread_count: number
  }[]
  // Available cross-post groups
  available_crosspost_groups?: {
    group_id: string
    group_name: string
    is_posted: boolean
  }[]
  // Saved excluded group IDs (groups user doesn't want to post to)
  excluded_group_ids?: string[]
  // Auto-reply message for this listing
  auto_reply_message?: string
  // Track which threads received auto-reply
  auto_replied_threads?: {
    thread_fbid: string
    replied_at: string
    message_sent: string
    reply_screenshot?: string
  }[]
  // Track failed reply attempts
  failed_reply_threads?: {
    thread_fbid: string
    failed_at: string
    message_attempted: string
    error: string
  }[]
  // Cross-posting
  is_posted_to_group?: boolean
  is_posted_to_marketplace?: boolean
  // Timestamps
  facebook_creation_time: string
  last_synced_at: string
  last_messages_synced_at?: string
  share_uri: string
  raw_data?: any
  full_raw_data?: any
  created_at?: string
  updated_at?: string
  // Pending edit (saved to DB, not yet pushed to Facebook)
  pending_edit?: {
    status: 'pending' | 'processing' | 'failed'
    changes?: Record<string, any>
    requested_at?: string
    error?: string | null
  } | null
  // Pending delete (saved to DB, not yet pushed to Facebook)
  pending_delete?: {
    status: 'pending' | 'processing' | 'failed'
    requested_at?: string
    error?: string | null
  } | null
}

export interface SyncedListingsResponse {
  success: boolean
  listings: FacebookSyncedListing[]
  pagination: {
    total: number
    page: number
    limit: number
    pages: number
  }
}

// Get connected Facebook profiles for marketplace
export const getFacebookProfiles = async (): Promise<FacebookProfilesResponse> => {
  const api = getAxios()
  const response = await api.get<FacebookProfilesResponse>(`${MARKETPLACE_URL}/facebook-profiles`)
  return response.data
}

// Trigger marketplace sync for a profile
export const syncMarketplaceListings = async (profileId: string): Promise<SyncResponse> => {
  const api = getAxios()
  const response = await api.post<SyncResponse>(`${MARKETPLACE_URL}/sync/${profileId}`)
  return response.data
}

// Get synced listings from Facebook
export const getSyncedListings = async (params?: {
  profile_id?: string
  is_live?: boolean
  page?: number
  limit?: number
}): Promise<SyncedListingsResponse> => {
  const api = getAxios()
  const response = await api.get<SyncedListingsResponse>(`${MARKETPLACE_URL}/synced-listings`, { params })
  return response.data
}

// Get single synced listing with raw data
export const getSyncedListing = async (id: string): Promise<{success: boolean; listing: FacebookSyncedListing}> => {
  const api = getAxios()
  const response = await api.get<{success: boolean; listing: FacebookSyncedListing}>(`${MARKETPLACE_URL}/synced-listings/${id}`)
  return response.data
}

// Update a synced listing on Facebook
export interface UpdateSyncedListingPayload {
  title?: string
  description?: string
  price?: number
  currency?: string
  category_id?: string
  latitude?: number
  longitude?: number
  condition?: string  // For items: new, used_like_new, used_good, used_fair
  vehicle_data?: {    // For vehicles
    make?: string
    model?: string
    year?: number
    mileage?: string
  }
  home_data?: {       // For properties (rental / home_sale)
    property_type?: string  // apartment | house | garden_apartment | penthouse | duplex | studio | room
  }
  photo_ids?: string[]      // Existing Facebook photo IDs to keep
  new_photo_urls?: string[] // New photos to upload to Facebook
}

export interface UpdateSyncedListingResponse {
  success: boolean
  message?: string
  listing_id?: string
  error?: string
  timestamp?: string
}

// AI-written (or improved) description for a synced listing. Returns text only;
// the user reviews it in the edit form and saves as usual.
export const aiSyncedListingDescription = async (id: string, data: {description?: string; instruction?: string}): Promise<{success: boolean; description?: string; details?: {property_type?: string; bedrooms?: number; bathrooms?: number; square_meters?: number}; error?: string}> => {
  const api = getAxios()
  const response = await api.post(`${MARKETPLACE_URL}/synced-listings/${id}/ai-description`, data, {timeout: 120000})
  return response.data
}

export const updateSyncedListing = async (id: string, data: UpdateSyncedListingPayload): Promise<UpdateSyncedListingResponse> => {
  const api = getAxios()
  const response = await api.put<UpdateSyncedListingResponse>(`${MARKETPLACE_URL}/synced-listings/${id}`, data)
  return response.data
}

// Delete a synced listing from Facebook
export const deleteSyncedListing = async (id: string): Promise<{success: boolean; message?: string}> => {
  const api = getAxios()
  const response = await api.delete<{success: boolean; message?: string}>(`${MARKETPLACE_URL}/synced-listings/${id}`)
  return response.data
}

// Reply to all missing conversations on a listing
export const replyAllMissing = async (id: string): Promise<{
  success: boolean
  message?: string
  request_id?: string
  conversations_count?: number
  auto_replied_count?: number
}> => {
  const api = getAxios()
  const response = await api.post<{
    success: boolean
    message?: string
    request_id?: string
    conversations_count?: number
    auto_replied_count?: number
  }>(`${MARKETPLACE_URL}/synced-listings/${id}/reply-all-missing`)
  return response.data
}

// Unmark a thread as replied (so it can be sent again)
export const unmarkThreadReplied = async (id: string, threadFbid: string): Promise<{success: boolean; message?: string}> => {
  const api = getAxios()
  const response = await api.delete<{success: boolean; message?: string}>(`${MARKETPLACE_URL}/synced-listings/${id}/auto-replied/${threadFbid}`)
  return response.data
}

// Sync messages for a specific listing
export const syncListingMessages = async (id: string): Promise<{
  success: boolean
  message?: string
  request_id?: string
}> => {
  const api = getAxios()
  const response = await api.post<{
    success: boolean
    message?: string
    request_id?: string
  }>(`${MARKETPLACE_URL}/synced-listings/${id}/sync-messages`)
  return response.data
}

// ===== Facebook Comments/Notifications =====

export interface CommentActor {
  name: string
  profile_picture?: string
  profile_url?: string
  facebook_id?: string
}

export interface FacebookComment {
  _id: string
  notif_id: string
  handled: boolean
  user?: {
    _id: string
    facebook_username?: string
    facebook_id?: string
  }
  created_at: string
  updated_at: string
  notif_type?: string
  message?: string
  url?: string
  post_id?: string
  is_bot_post?: boolean
  actors?: CommentActor[]
  icon_url?: string
  timestamp?: string
}

export interface CommentsResponse {
  success: boolean
  comments: FacebookComment[]
  pagination: {
    total: number
    page: number
    limit: number
    pages: number
    scanned?: number
    truncated?: boolean
  }
}

export interface CommentStatsResponse {
  success: boolean
  stats: {
    total: number
    pending: number
    handled: number
    all_total?: number
    all_pending?: number
    all_handled?: number
    bot_posts_total?: number
    external_posts_total?: number
    scanned?: number
    truncated?: boolean
  }
}

// Get Facebook comments for auto-reply (group comments on bot posts by default)
export const getComments = async (params?: {
  profile_id?: string
  handled?: boolean
  notif_type?: string
  bot_posts_only?: boolean
  page?: number
  limit?: number
}): Promise<CommentsResponse> => {
  const api = getAxios()
  const response = await api.get<CommentsResponse>(`${MARKETPLACE_URL}/comments`, { params })
  return response.data
}

// Get comment statistics (group comments on bot posts by default)
export const getCommentStats = async (params?: {
  bot_posts_only?: boolean
}): Promise<CommentStatsResponse> => {
  const api = getAxios()
  const response = await api.get<CommentStatsResponse>(`${MARKETPLACE_URL}/comments/stats`, { params })
  return response.data
}

// Mark comment as handled
export const markCommentHandled = async (id: string, handled: boolean = true): Promise<{success: boolean}> => {
  const api = getAxios()
  const response = await api.put<{success: boolean}>(`${MARKETPLACE_URL}/comments/${id}/handled`, { handled })
  return response.data
}

// Trigger sync of notifications from Facebook
export const syncComments = async (profileId?: string): Promise<{success: boolean; message?: string}> => {
  const api = getAxios()
  const response = await api.post<{success: boolean; message?: string}>(`${MARKETPLACE_URL}/comments/sync`, { profile_id: profileId })
  return response.data
}

// ===== My Listings (created via UI) =====

export interface MyListing {
  _id: string
  account_id: string
  profile_ids: string[]
  profile_names: string[]
  listing_type: 'item' | 'vehicle' | 'home_rent' | 'home_sale'
  title: string
  description?: string
  price: number
  currency: string
  category_id: string
  category_name?: string
  latitude?: number
  longitude?: number
  address?: string
  photo_ids: string[]
  photo_urls: string[]
  item_attributes?: {
    condition?: string
    brand?: string
    color?: string
    size?: string
    [key: string]: any
  }
  vehicle_data?: any
  home_data?: any
  status: 'draft' | 'pending' | 'posted' | 'failed' | 'deleted'
  facebook_listing_id?: string
  facebook_listing_url?: string
  posted_at?: string
  error_message?: string
  retry_count: number
  created_at: string
  updated_at: string
}

export interface MyListingsResponse {
  success: boolean
  listings: MyListing[]
  pagination: {
    total: number
    page: number
    limit: number
    pages: number
  }
}

// Get user's created listings
export const getMyListings = async (params?: {
  status?: 'pending' | 'posted' | 'failed' | 'all'
  page?: number
  limit?: number
}): Promise<MyListingsResponse> => {
  const api = getAxios()
  const response = await api.get<MyListingsResponse>(`${MARKETPLACE_URL}/my-listings`, { params })
  return response.data
}

// Retry posting a failed listing
export const retryListing = async (id: string): Promise<{success: boolean; message?: string; listing_id?: string}> => {
  const api = getAxios()
  const response = await api.post<{success: boolean; message?: string; listing_id?: string}>(`${MARKETPLACE_URL}/my-listings/${id}/retry`)
  return response.data
}

// Delete a listing from database
export const deleteMyListing = async (id: string): Promise<{success: boolean; message?: string}> => {
  const api = getAxios()
  const response = await api.delete<{success: boolean; message?: string}>(`${MARKETPLACE_URL}/my-listings/${id}`)
  return response.data
}
